import * as React from 'react';

import { cn } from '../lib/cn';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export interface CurrencyOption {
  code: string;
  symbol: string;
  label?: string;
}

export const DEFAULT_CURRENCIES: CurrencyOption[] = [
  { code: 'CZK', symbol: 'Kč', label: 'Czech koruna' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'USD', symbol: '$', label: 'US dollar' },
  { code: 'GBP', symbol: '£', label: 'British pound' },
];

export interface MoneyValue {
  amount: string; // string to preserve decimals as typed
  currency: string;
}

export interface MoneyInputProps {
  value?: MoneyValue;
  onChange?: (v: MoneyValue) => void;
  currencies?: CurrencyOption[];
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  min?: number;
  max?: number;
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value = { amount: '', currency: 'CZK' },
      onChange,
      currencies = DEFAULT_CURRENCIES,
      invalid,
      disabled,
      placeholder = '0.00',
      className,
      id,
      min,
      max,
    },
    ref,
  ) => {
    return (
      <div
        className={cn(
          'flex h-9 items-center gap-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] pl-3 pr-1',
          'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <input
          ref={ref}
          id={id}
          type="number"
          inputMode="decimal"
          step="0.01"
          min={min}
          max={max}
          disabled={disabled}
          value={value.amount}
          onChange={(e) => onChange?.({ ...value, amount: e.target.value })}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          className="h-full flex-1 bg-transparent text-sm tabular-nums text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-muted)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <Select
          value={value.currency}
          onValueChange={(c) => onChange?.({ ...value, currency: c })}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 w-auto gap-1 border-0 bg-transparent px-2 text-sm focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {currencies.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <span className="inline-flex items-center gap-2">
                  <span>{c.code}</span>
                  <span className="text-[var(--color-fg-muted)]">{c.symbol}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  },
);
MoneyInput.displayName = 'MoneyInput';
